import React, { useEffect, useState, useRef } from "react";
import { Typography } from "antd";

const { Title, Text } = Typography;

const ConsoleViewer = ({ wsUrl, healthKey }) => {
  const [logs, setLogs] = useState([]);
  const [status, setStatus] = useState("checking");
  const scrollRef = useRef(null);

  useEffect(() => {
    let ws;
    let isCancelled = false;

    const tryConnect = async () => {
      try {
        const res = await fetch("/health");
        const json = await res.json();
        if (!json[healthKey]) throw new Error(`${healthKey} aún no está disponible`);

        setStatus("connecting");
        ws = new WebSocket(wsUrl);

        ws.onopen = () => !isCancelled && setStatus("connected");
        ws.onerror = () => !isCancelled && setStatus("error");
        ws.onclose = () => !isCancelled && setStatus("closed");

        ws.onmessage = async (e) => {
          if (!isCancelled) {
            const text = typeof e.data === "string" ? e.data : await e.data.text?.();
            if (!text?.includes("Keep-alive")) {
              setLogs((prev) => [...prev.slice(-200), text]);
            }
          }
        };
      } catch {
        if (!isCancelled) setStatus("error");
      }
    };

    tryConnect();

    return () => {
      isCancelled = true;
      ws?.close();
    };
  }, [wsUrl, healthKey]);

  useEffect(() => {
    scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight);
  }, [logs]);

  const renderStatus = () => {
    switch (status) {
      case "checking":
        return <div style={{ color: "#888" }}>⏳ Verificando disponibilidad...</div>;
      case "connecting":
        return <div style={{ color: "#888" }}>⏳ Conectando al WebSocket...</div>;
      case "error":
      case "closed":
        return (
          <div style={{ color: "#f00" }}>
            ❌ No se pudo conectar a <code>{wsUrl}</code>
          </div>
        );
      case "connected":
        return (
          <div style={{ color: "#0f0" }}>🟢 Conectado, esperando logs...</div>
        );
      default:
        return null;
    }
  };

  return (
    <div
      ref={scrollRef}
      style={{
        backgroundColor: "#000",
        color: "#0f0",
        fontSize: "0.75rem",
        padding: "0.75rem 1rem",
        height: 280,
        overflowY: "auto",
        borderRadius: "8px",
        fontFamily: "monospace",
        boxShadow: "inset 0 0 4px rgba(0,255,0,0.1)",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      }}
    >
      {status === "connected" && logs.length > 0
        ? logs.map((line, idx) => <div key={idx}>{line}</div>)
        : renderStatus()}
    </div>
  );
};

export default function LogSistema() {
  const protocol = window.location.protocol === "https:" ? "wss" : "ws";
  const host = window.location.host;
  const wsBase = `${protocol}://${host}/ws/stream`;

  const [reconnectKey, setReconnectKey] = useState(0);

  // 🔁 Reconecta al volver a la pestaña
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === "visible") {
        setReconnectKey((prev) => prev + 1);
      }
    };
    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }, []);

  return (
    <div style={{ padding: "2rem", maxWidth: "100%", margin: "0 auto" }}>
      <Title level={3}>🧾 Consola del Sistema en Tiempo Real</Title>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem" }}>
        <div style={{ flex: "1 1 32%", minWidth: 300, display: "flex", flexDirection: "column" }}>
          <Text strong>🧠 FastAPI</Text>
          <ConsoleViewer key={`fastapi-${reconnectKey}`} wsUrl={`${wsBase}/fastapi`} healthKey="fastapi" />
        </div>

        <div style={{ flex: "1 1 32%", minWidth: 300, display: "flex", flexDirection: "column" }}>
          <Text strong>🚀 Launcher</Text>
          <ConsoleViewer key={`launcher-${reconnectKey}`} wsUrl={`${wsBase}/launcher`} healthKey="launcher" />
        </div>

        <div style={{ flex: "1 1 32%", minWidth: 300, display: "flex", flexDirection: "column" }}>
          <Text strong>🖧 NGINX</Text>
          <ConsoleViewer key={`nginx-${reconnectKey}`} wsUrl={`${wsBase}/nginx`} healthKey="nginx" />
        </div>
      </div>
    </div>
  );

}
